import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsMounted } from 'usehooks-ts';

import useUrlParams from '@/utils/useUrlParams';

import type { ListDisplayType } from '../ListSwitcher';
import { TagFilters } from '../TagFilters';
import { Resource, TagFilter } from '../TagFilters/types';

interface TagsSearchProps {
  filters: TagFilter[];
  extra?: React.ReactNode;
  queryName?: string;
  resource?: Resource[];
  getData?: (params: any) => void;
  refresh?: () => void;
  onSearch?: () => any;
  /** 列表/卡片切换（透传 TagFilters，与硬件资产等页一致） */
  displayType?: ListDisplayType;
  onChangeDisplayType?: (next: ListDisplayType) => void;
  /** 与默认 marginBottom 合并，便于筛选条与表格/卡片区留白对齐 */
  tagBarStyle?: CSSProperties;
  /** 写入 URL 前最后一次整理参数，兼容卡片视图不持久化分页等特殊场景。 */
  formatUrlParams?: (params: Record<string, any>) => Record<string, any>;
  /** 强制隐藏数据中心切换器（弹窗等局部场景不需要切 DC） */
  hideDataCenterSwitcher?: boolean;
}

/** 兼容旧组件的迁移过渡方案, 新需求直接结合配置使用新组件 TagFilters,参考说明文档: src/components/TagFilters/TagFilters-使用说明.md */
export function TagsSearch({
  getData,
  filters,
  refresh,
  extra,
  queryName = 'query',
  resource,
  onSearch,
  displayType,
  onChangeDisplayType,
  tagBarStyle,
  formatUrlParams,
  hideDataCenterSwitcher = false,
}: TagsSearchProps) {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { setParam, getParam } = useUrlParams();
  const [values, setValues] = useState<Record<string, any>>({});

  // 筛选条件变化的回调
  const onValuesChange = (newValues: any) => {
    setValues(newValues);
    if (getData) {
      const o = onSearch?.() || {};
      // 重置到第一页
      getData({ ...newValues, ...o });
      const query = JSON.parse(getParam(queryName, '{}'));
      // 同步到 URL 参数
      const vals: Record<string, any> = {};
      Object.entries({ ...query, ...newValues, ...o, page: 1 }).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '' && k in newValues) {
          vals[k] = v;
        }
      });
      setParam(queryName, JSON.stringify(formatUrlParams ? formatUrlParams(vals) : vals));
    }
  };

  // 初始化获取 URL 参数,
  useEffect(() => {
    if (isMounted()) {
      const query = JSON.parse(getParam(queryName, '{}'));
      setValues(query);
    }
  }, [isMounted, getParam, queryName]);

  const tagFilters = (
    <TagFilters
      filters={filters}
      displayType={displayType}
      onChangeDisplayType={onChangeDisplayType}
      onRefresh={() => {
        refresh?.();
        getData?.(values);
      }}
      placeholder={t('common:search')}
      values={values}
      onChange={onValuesChange}
      extra={extra}
      resource={resource}
      style={{ ...tagBarStyle }}
      hideDataCenterSwitcher={hideDataCenterSwitcher}
    />
  );

  return tagFilters;
}
